import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/theme-toggle";
import { Eye, EyeOff, Loader2, Shield, KeyRound } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof loginSchema>;

const STAFF_2FA_PENDING_KEY = "staff_2fa_pending";

export default function StaffPortalPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  
  // Restore 2FA pending state from sessionStorage on mount (for mobile app switching)
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem(STAFF_2FA_PENDING_KEY);
      if (pending === "true") {
        setRequires2FA(true);
      }
    } catch {
      // sessionStorage not available
    }
  }, []);
  
  // Save 2FA pending state to sessionStorage
  const set2FAPending = (pending: boolean) => {
    setRequires2FA(pending);
    try {
      if (pending) {
        sessionStorage.setItem(STAFF_2FA_PENDING_KEY, "true");
      } else {
        sessionStorage.removeItem(STAFF_2FA_PENDING_KEY);
      }
    } catch {
      // sessionStorage not available
    }
  };
  
  // Clear 2FA state on successful login or explicit cancel
  const clear2FAState = () => {
    setRequires2FA(false);
    setTwoFactorToken("");
    try {
      sessionStorage.removeItem(STAFF_2FA_PENDING_KEY);
    } catch {
      // sessionStorage not available
    }
  };

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: LoginForm) => {
      const response = await apiRequest("POST", "/api/auth/staff/login", data);
      return response.json();
    },
    onSuccess: (data) => {
      if (data.requiresTwoFactor) {
        set2FAPending(true);
        toast({
          title: "Verification Required",
          description: "Please enter your authenticator code to continue.",
        });
      } else {
        clear2FAState();
        queryClient.setQueryData(["/api/auth/user"], data.user);
        toast({
          title: "Welcome back",
          description: "You have successfully logged in.",
        });
        setLocation("/admin");
      }
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Access denied",
        description: error.message || "Invalid credentials or insufficient permissions",
      });
    },
  });

  const verify2FAMutation = useMutation({
    mutationFn: async ({ token, trustDevice }: { token: string; trustDevice: boolean }) => {
      const response = await apiRequest("POST", "/api/auth/staff/2fa-verify", { token, trustDevice });
      return response.json();
    },
    onSuccess: (data) => {
      clear2FAState();
      queryClient.setQueryData(["/api/auth/user"], data.user);
      toast({
        title: "Welcome back",
        description: data.usedBackupCode 
          ? "Logged in using backup code. Consider regenerating your backup codes."
          : "You have successfully logged in.",
      });
      setLocation("/admin");
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: error.message || "Invalid verification code",
      });
      setTwoFactorToken("");
    },
  });

  const onSubmit = (data: LoginForm) => {
    loginMutation.mutate(data);
  };

  const handleVerify2FA = () => {
    if (twoFactorToken.length >= 6) {
      verify2FAMutation.mutate({ token: twoFactorToken, trustDevice });
    }
  };

  if (requires2FA) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="absolute right-4 top-4">
          <ThemeToggle />
        </div>

        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <KeyRound className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app, or an 8-character backup code.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center space-y-4">
              <InputOTP
                maxLength={8}
                value={twoFactorToken}
                onChange={setTwoFactorToken}
                data-testid="input-staff-2fa-code"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                  <InputOTPSlot index={6} />
                  <InputOTPSlot index={7} />
                </InputOTPGroup>
              </InputOTP>
              <p className="text-xs text-muted-foreground">
                6 digits for authenticator code, 8 characters for backup code
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="trust-device"
                checked={trustDevice}
                onCheckedChange={(checked) => setTrustDevice(checked === true)}
                data-testid="checkbox-staff-trust-device"
              />
              <label
                htmlFor="trust-device"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Trust this device for 30 days
              </label>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleVerify2FA}
                disabled={twoFactorToken.length < 6 || verify2FAMutation.isPending}
                className="w-full"
                data-testid="button-staff-verify-2fa"
              >
                {verify2FAMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Verify
              </Button>
              <Button
                variant="ghost"
                onClick={clear2FAState}
                data-testid="button-staff-back-to-login"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Staff Portal</CardTitle>
          <CardDescription>
            Authorized personnel only
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        data-testid="input-staff-email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          data-testid="input-staff-password"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-1/2 -translate-y-1/2 no-default-hover-elevate no-default-active-elevate"
                          onClick={() => setShowPassword(!showPassword)}
                          data-testid="button-staff-toggle-password"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={loginMutation.isPending}
                data-testid="button-staff-login"
              >
                {loginMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Sign In
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
